import _isNil from 'lodash/isNil';
import _isString from 'lodash/isString';
import _isEmpty from 'lodash/isEmpty';
import Parameter, { BoolClause } from './Parameter';
import ParameterKeyValue from './ParameterKeyValue';
import { Tag } from '../types';

const isValidStringValue = (configuredValue: string): boolean =>
  !_isNil(configuredValue) &&
  _isString(configuredValue) &&
  !_isEmpty(configuredValue);

/**
 * Codes can be input as a single value or csv, standardize code input to an array trimmed strings
 *
 * @param {string} code
 * @returns {string[]}
 */
function formatCode(code: string): string[] {
  return isValidStringValue(code)
    ? code
        .split(',')
        .map(s => s.trim())
        .filter(s => !_isEmpty(s))
    : [];
}

export class SystemCodePair {
  private _system: string;
  private _code: string[];

  constructor(system: string, code: string) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    this._system = isValidStringValue(system) ? system.trim() : null;
    this._code = formatCode(code);
  }

  get isValid() {
    return this._system !== null && this._code.length > 0;
  }

  get system() {
    return this._system;
  }

  get code() {
    return this._code;
  }

  get FSSClause(): BoolClause {
    return {
      bool: {
        // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
        must: [
          this._system
            ? { term: { 'meta.tag.system.keyword': this._system } }
            : null,
          {
            bool: {
              should: this._code.map(_ => ({
                term: { 'meta.tag.code.keyword': _ },
              })),
            },
          },
        ].filter(Boolean),
      },
    };
  }
}

export type SystemCodeHashMap = { [system: string]: string[] };

/**
 * Turns System code pairs to a map of system -> array of systems codes
 *
 * @returns {SystemCodeHashMap}
 */
function codePairToSystemCodeMap(
  tags: (SystemCodePair | NotCodeValue)[] = [],
): SystemCodeHashMap {
  const systemCodeMap: SystemCodeHashMap = {};
  tags
    .filter(tag => tag.isValid)
    .forEach(tag => {
      if (
        _isNil(systemCodeMap[tag.system]) ||
        !systemCodeMap[tag.system].length
      ) {
        systemCodeMap[tag.system] = tag.code;
      } else {
        const combinedList = [...systemCodeMap[tag.system], ...tag.code];
        const dedupedList = [...new Set(combinedList)];
        systemCodeMap[tag.system] = dedupedList;
      }
    });

  return systemCodeMap;
}

/**
 * system must be enumerated for codes in the following format
 * system1|code1,system1|code2,system1|code3
 *
 * @param {string} system
 * @param {string[]} codeValues
 * @returns {string}
 */
function enumerateSystemValue(system: string, codeValues: string[]): string {
  return codeValues.map(codeValue => `${system}|${codeValue}`).join(',');
}

/**
 * Filter for fhir meta tag
 *
 * @export
 * @class TagFilter
 * @extends {Parameter}
 */
export default class TagFilter extends Parameter {
  private _tags: SystemCodePair[];

  constructor(useTagFilter: boolean, tags: Tag[] = []) {
    super();

    if (!useTagFilter || _isEmpty(tags)) {
      this._tags = [];
    } else {
      this._tags = tags
        .map(
          (filter: Tag = {} as Tag): SystemCodePair =>
            new SystemCodePair(filter.system, filter.code),
        )
        .filter(tag => tag.isValid);
    }
  }

  get parameterKeyValue(): ParameterKeyValue {
    if (this._tags.length === 0) return new ParameterKeyValue();

    // tags of the same type should be combined by columns to be or'd
    const systemCodeMap = codePairToSystemCodeMap(this._tags);
    const parameterValue = Object.keys(systemCodeMap).map(system =>
      enumerateSystemValue(system, systemCodeMap[system]),
    );
    return new ParameterKeyValue('_tag', parameterValue);
  }

  async getFSSClause(): Promise<BoolClause | null> {
    if (this._tags.length === 0) return null;

    return {
      bool: {
        must: this._tags.map(tag => tag.FSSClause),
      },
    };
  }
}

export class NotCodeValue {
  private _system: string;
  private _code: string[];

  constructor(system: string, code: string) {
    this._system = isValidStringValue(system) ? system.trim() : '';
    this._code = formatCode(code);
  }

  get isValid() {
    return this._system !== null && this._code.length > 0;
  }

  get system() {
    return this._system;
  }

  get code() {
    return this._code;
  }

  get FSSClause(): BoolClause {
    return {
      bool: {
        must_not: [
          {
            bool: {
              should: this._code.map(_ => ({
                term: { 'meta.tag.code.keyword': _ },
              })),
            },
          },
        ].filter(Boolean),
      },
    };
  }
}

/**
 * Filter for not fhir meta tag code value
 *
 * @export
 * @class TagFilterNotCodeValue
 * @extends {Parameter}
 */
export class TagFilterNotCodeValue extends Parameter {
  private _tags: NotCodeValue[];

  constructor(useNotTagFilter: boolean = false, tags: Tag[] = []) {
    super();

    if (!useNotTagFilter || _isEmpty(tags)) {
      this._tags = [];
    } else {
      this._tags = tags
        .map(
          (filter: Tag = {} as Tag): NotCodeValue =>
            new NotCodeValue(filter.system, filter.code),
        )
        .filter(tag => tag.isValid);
    }
  }

  // Determine query params for patient-service fhir search
  // This path is used when feature toggle `subjectViewerFSSQueries` is enabled
  get parameterKeyValue(): ParameterKeyValue {
    if (this._tags.length === 0) return new ParameterKeyValue();

    // tags of the same type should be combined by columns to be or'd
    const systemCodeMap = codePairToSystemCodeMap(this._tags);
    const parameterValue = Object.keys(systemCodeMap).map(system =>
      enumerateSystemValue(system, systemCodeMap[system]),
    );
    return new ParameterKeyValue('_tag:not', parameterValue);
  }

  // Determine fss clause for fhir-search-service search (elastic search like syntax)
  async getFSSClause(): Promise<BoolClause | null> {
    if (this._tags.length === 0) return null;

    return {
      bool: {
        must: this._tags.map(tag => tag.FSSClause),
      },
    };
  }
}
